import { Component, OnInit, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { ApiClient, ApiClientData } from '../../home-types';
import { Visibility, Truth } from '../../../common/common-types';
import { cardOpen, CardState } from '../../../common/animations';
import { SystemGlobalsService } from '../../../system-globals.service';
import { plainToClass } from 'class-transformer';
import { SimpleModalComponent } from '../../../common/modal/simple-modal/simple-modal.component';
import { FormGroup, NgForm } from '@angular/forms';
import { FormValidationError, getFormValidationErrors } from '../../../common/get-form-validation-errors';
import { HomeService } from '../../home.service';
import { ToastService } from 'ng-uikit-pro-standard';
import { TranslationService } from '../../../common/translation.service';
import { Observable } from 'rxjs';
import { ClipboardService } from 'ngx-clipboard';

@Component({
  selector: 'api-client',
  templateUrl: './api-client.component.html',
  styleUrls: ['./api-client.component.scss'],
  animations: [
    cardOpen
  ]
})
export class ApiClientComponent implements OnInit, Visibility {

  @ViewChild('confirmModal') confirmModal: SimpleModalComponent;
  @ViewChild('clientInfoModal') clientInfoModal: SimpleModalComponent;
  @ViewChild('clientForm') public reportForm: NgForm;
  
  @Input() clientData: ApiClientData;
  @Input() setVisible: Observable<boolean>;

  @Output() onCreate: EventEmitter<ApiClientData> = new EventEmitter<ApiClientData>();
  @Output() onUpdate: EventEmitter<ApiClientData> = new EventEmitter<ApiClientData>();
  @Output() onDelete: EventEmitter<ApiClientData> = new EventEmitter<ApiClientData>();

  private _visible: boolean = false;

  client: ApiClient;
  state: CardState = CardState.Closed;
  showDeleteModal: boolean = false;
  isBusySaving: boolean = false;

  public urlRegex: RegExp = /^https?:\/\/[a-zA-Z0-9-_]+(?:\.[a-zA-Z0-9-_]+)*(?::\d+)?(?:\/[^\s]*)?$/;

  modalHeading: string = '';
  modalOkButtonLabel: string = '';
  modalContent: string = '';
  modalCallback: (arg?: any) => void;

  constructor(
    public globals: SystemGlobalsService,
    public homeService: HomeService,
    public toast: ToastService,
    public translation: TranslationService,
    public clipboardService: ClipboardService
  ) { }

  @Input() set visible(isVisible: boolean) {
    if (isVisible == null) return;

    this._visible = isVisible;
    this.updateState();
  }

  get visible(): boolean {
    return this._visible;
  }

  ngOnInit() {
    this.setupFormErrorNames();

    if (!this.clientData) this.createNewClientData();

    this.client = plainToClass(ApiClient, this.clientData);

    if (this.client.isNew || this.clientData._startOpen) {
      this._visible = true;
      this.state = CardState.Open;
    }

    if (this.setVisible) this.setVisible.subscribe(visible => this.visible = visible);
  }

  updateState() {
    setTimeout(() => {
      if (this._visible) this.state = CardState.Open;
      else this.state = CardState.Closed;
    }, 100);
  }

  hide() {
    this.visible = false;
  }

  show() {
    this.visible = true;    
  }

  isVisible() {
    return this.visible == true;
  }

  toggleVisible() {
    if (!this.client.isNew) this.visible = !this.visible;
  }

  save() {
    this.isBusySaving = true;

    const payload = this.buildSavePayload();

    this.homeService.saveApiApp(payload).subscribe(
      apiClientData => {
        if (!this.isSuccessfulSaveResponse(apiClientData)) {
          this.isBusySaving = false;
          this.toast.error(this.extractErrorMessage(apiClientData), 'Error', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});
          return;
        }
        apiClientData.maskedSecret = Truth.False;
        this._onSave(apiClientData);

        this.isBusySaving = false;
      },
      error => {
        this.isBusySaving = false;
        this.toast.error(this.extractErrorMessage(error), 'Error', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});
      }
    );
  }

  private buildSavePayload(): ApiClientData {
    const payload = { ...this.client } as ApiClientData;
    payload.simpleClient = this.toTruth(this.client.simpleClient);
    payload.callbackEnabled = this.toTruth(this.client.callbackEnabled);

    if (payload.simpleClient === Truth.True) {
      payload.redirect_uri = '';
    }
    if (payload.callbackEnabled !== Truth.True) {
      payload.callbackUri = '';
    }

    return payload;
  }

  private toTruth(value: Truth | boolean | number | string): Truth {
    if (value === true || value === Truth.True || value === 1 || value === '1' || value === 'true') {
      return Truth.True;
    }

    return Truth.False;
  }

  private isSuccessfulSaveResponse(apiClientData: ApiClientData | any): boolean {
    if (!apiClientData) {
      return false;
    }

    if (apiClientData.success === false || apiClientData.error) {
      return false;
    }

    return !!apiClientData.client_id;
  }

  private extractErrorMessage(error: any): string {
    if (!error) {
      return 'Unable to save API application.';
    }

    if (typeof error === 'string') {
      return error;
    }

    const body = error.error ?? error;
    if (typeof body === 'string') {
      return body;
    }

    if (body && typeof body.error === 'string') {
      return body.error;
    }

    if (body && typeof body.message === 'string') {
      return body.message;
    }

    if (body && body.errors && typeof body.errors === 'object') {
      const messages: string[] = [];
      Object.keys(body.errors).forEach(key => {
        const fieldErrors = body.errors[key];
        if (Array.isArray(fieldErrors)) {
          messages.push(...fieldErrors);
        } else if (typeof fieldErrors === 'string') {
          messages.push(fieldErrors);
        }
      });
      if (messages.length) {
        return messages.join(' ');
      }
    }

    return 'Unable to save API application.';
  }

  _onSave(apiClientData: ApiClientData) {
    if (this.client.isNew) {
      this.createNewClientData();
      this.onCreate.emit(apiClientData);
    }
    else {
      this.clientData = apiClientData;
      this.onUpdate.emit(apiClientData);
    }

    this.client = plainToClass(ApiClient, this.clientData);    
  }

  _onDelete() {
    this.onDelete.emit(this.client);
  }

  deleteConfirm() {
    this.modalCallback = this.delete;
    this.modalContent = 'Are you sure you want to delete this application?';
    this.modalHeading = 'Delete Application';
    this.modalOkButtonLabel = 'Delete';

    this.confirmModal.visible = true;
  }

  delete(self: ApiClientComponent) {
    self.homeService.deleteApiApp(self.client.client_id).subscribe(
      success => {
        self._onDelete();
        self.toast.success('Deleted successfully', 'Success', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});
      },
      error => {
        self.toast.error(error, 'Error', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});
      }
    );
  }

  getAllErrors(form: FormGroup): FormValidationError[] {
    return getFormValidationErrors(form.controls);
  }

  setupFormErrorNames() {
    this.translation.addDictionaryWord('api-client--redirect_uri', 'Redirect Url');
    this.translation.addDictionaryWord('api-client--companyName', 'Company Name');
    this.translation.addDictionaryWord('api-client--applicationName', 'Application Name');
    this.translation.addDictionaryWord('api-client--description', 'Description');
    this.translation.addDictionaryWord('api-client--callbackUri', 'Callback (Webhook) Url');
  }

  createNewClientData() {
    this.clientData = {
      client_id: '',
      redirect_uri: '',
      companyName: '',
      applicationName: '',
      description: '',
      created: '',
      modified: '',
      callbackUri: '',
      callbackEnabled: Truth.False,
      clientCount: 0,
      simpleClient: Truth.False,
      maskedSecret: Truth.True,
    };
  }

  copyClientId() {
    this.clipboardService.copyFromContent(this.client.client_id);
    this.toast.success('Copied to clipboard', 'Success', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});
  }

  copyClientSecret() {
    this.clipboardService.copyFromContent(this.client.client_secret);
    this.toast.success('Copied to clipboard', 'Success', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});
  }

  revokeTokensConfirm() {
    this.modalCallback = this.revokeTokens;
    this.modalContent = 'Are you sure you want to revoke all access tokens for this application? Revoking access tokens will effectively disable access to our system from your application.';
    this.modalHeading = 'Revoke All Access Tokens';
    this.modalOkButtonLabel = 'Revoke Access Tokens';

    this.confirmModal.visible = true;
  }

  regenerateSecretConfirm() {
    this.modalCallback = this.regenerateSecret;
    this.modalContent = 'Are you sure you want to regenerate your client secret?Regenerating your client secret should only be used in case of security concerns.' +
      ' You will not be able to issue new access or refresh tokens until you substitute your existing client secret with the new client secret in your app.';
    this.modalHeading = 'Regenerate Client Secret';
    this.modalOkButtonLabel = 'Regenerate Client Secret';

    this.confirmModal.visible = true;
  }

  revokeTokens(self: ApiClientComponent) {
    self.homeService.revokeAccessTokens(self.client.client_id).subscribe(
      success => {
        self.client.clientCount = 0;
        self.toast.success('Revoked all access tokens', 'Success', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});
      },
      error => {
        self.toast.error(error, 'Error', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});
      }
    );
  }

  regenerateSecret(self: ApiClientComponent) {
    self.homeService.regenerateClientSecret(self.client.client_id).subscribe(
      secretData => {
        self.clientData.client_secret = secretData['secret'];
        self.client.client_secret = secretData['secret'];
        self.client.maskedSecret = Truth.False;

        self.toast.success('Regenerated client secret', 'Success', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});

        setTimeout(() => self.clientInfoModal.visible = true, 1000);
      },
      error => {
        self.toast.error(error, 'Error', {positionClass: 'md-toast-bottom-right', toastClass: 'opaque'});
      }
    );
  }
}
